import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchPost, fetchPosts } from '@/lib/api';

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function readTime(content) {
  if (!content) return '1 min read';
  const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
  const mins = Math.max(1, Math.ceil(words / 200));
  return `${mins} min read`;
}

function PostSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="bg-muted h-72 md:h-[28rem] mb-8" />
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="bg-muted rounded h-4 w-32 mb-6" />
        <div className="bg-muted rounded h-12 w-3/4 mb-4" />
        <div className="bg-muted rounded h-4 w-48 mb-12" />
        <div className="space-y-3">
          <div className="bg-muted rounded h-4 w-full" />
          <div className="bg-muted rounded h-4 w-full" />
          <div className="bg-muted rounded h-4 w-5/6" />
          <div className="bg-muted rounded h-4 w-full" />
          <div className="bg-muted rounded h-4 w-3/4" />
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
      <h1 className="font-heading text-4xl font-bold text-primary mb-4">
        Post Not Found
      </h1>
      <p className="text-muted-foreground mb-8 text-lg">
        This article doesn't exist or has been removed.
      </p>
      <Button asChild className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
        <Link to="/blog">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>
      </Button>
    </div>
  );
}

function RelatedPosts({ currentSlug }) {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetchPosts()
      .then((all) => setPosts(all.filter((p) => p.slug !== currentSlug).slice(0, 3)))
      .catch(() => {});
  }, [currentSlug]);

  if (posts.length === 0) return null;

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-heading text-2xl font-bold text-primary">
            More Articles
          </h2>
          <Link
            to="/blog"
            className="text-sm font-medium text-accent hover:underline inline-flex items-center gap-1"
          >
            View all <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {posts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group block"
            >
              {post.cover_image ? (
                <div className="overflow-hidden rounded-xl mb-4">
                  <img
                    src={post.cover_image}
                    alt={post.title}
                    className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="w-full h-44 bg-primary/5 rounded-xl mb-4 flex items-center justify-center">
                  <span className="font-heading text-xl text-primary/20">Dockside</span>
                </div>
              )}
              <h3 className="font-heading text-lg font-bold text-primary group-hover:text-accent transition-colors">
                {post.title}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {formatDate(post.publishedAt)}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchPost(slug)
      .then(setPost)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <PostSkeleton />;
  if (notFound || !post) return <NotFound />;

  return (
    <article data-testid="blog-post-page">
      {/* Cover image — full-width hero */}
      {post.cover_image && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="relative w-full h-72 md:h-[28rem] overflow-hidden"
        >
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
        </motion.div>
      )}

      {/* Post header */}
      <section className={post.cover_image ? '-mt-24 relative z-10' : 'pt-16 md:pt-24'}>
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Back link */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to blog
            </Link>

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-4">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-medium uppercase tracking-wider text-accent"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pb-8 border-b border-border/50">
              {post.author && (
                <span className="font-medium text-foreground">{post.author}</span>
              )}
              {post.publishedAt && (
                <>
                  <span>&middot;</span>
                  <span>{formatDate(post.publishedAt)}</span>
                </>
              )}
              <span>&middot;</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {readTime(post.content)}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Post content */}
      <section className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-primary
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-foreground/80 prose-p:leading-relaxed
                prose-a:text-accent prose-a:no-underline hover:prose-a:underline
                prose-img:rounded-xl prose-img:shadow-md
                prose-blockquote:border-accent/40 prose-blockquote:bg-accent/5 prose-blockquote:rounded-r-xl prose-blockquote:py-1 prose-blockquote:px-6
                prose-strong:text-foreground
                prose-code:text-accent prose-code:bg-accent/10 prose-code:rounded prose-code:px-1.5 prose-code:py-0.5 prose-code:before:content-none prose-code:after:content-none
                prose-pre:bg-primary prose-pre:text-primary-foreground prose-pre:rounded-xl
                [&_img[data-align=left]]:mr-auto [&_img[data-align=left]]:ml-0
                [&_img[data-align=center]]:mx-auto
                [&_img[data-align=right]]:ml-auto [&_img[data-align=right]]:mr-0
                [&_img]:block"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </motion.div>
        </div>
      </section>

      {/* Author / share bar */}
      <section className="pb-8">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="border-t border-b border-border/50 py-6 flex items-center justify-between">
            <div>
              {post.author && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Written by </span>
                  <span className="font-medium text-foreground">{post.author}</span>
                </p>
              )}
            </div>
            <Link
              to="/blog"
              className="text-sm font-medium text-accent hover:underline inline-flex items-center gap-1"
            >
              More articles <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related posts */}
      <RelatedPosts currentSlug={slug} />

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Need a Website for Your Business?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
              From {'\u00a3'}99/month with nothing to pay upfront. Custom design, hosting,
              security, and support all included.
            </p>
            <Button
              asChild
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg"
            >
              <Link to="/contact">Get a Quote</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </article>
  );
};

export default BlogPost;
